import { twMerge } from 'tailwind-merge';
import { useToggle } from '@/hooks/useToggle';
import { useString } from '@/hooks/useString';
import { downloadFileCollection } from '@/utils/files';
import { useAppState } from '@/providers/AppStateProvider';
import Divider from '@/components/Divider';
import OptionButton from '@/components/ViewerHeader/OptionButton';
import IconButton from '@/components/IconButton';
import {
  CloseIcon,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  MessagesIcon,
} from '@/icons';
import DownloadToaster from '@/toasters/DownloadToaster';
import FileCollectionDialog from '@/dialogs/FileCollectionDialog';
import DeleteFileCollectionDialog from '@/dialogs/DeleteFileCollectionDialog';
import { classes } from './utils';

export type FileCollectionControlsProps = {
  showTimeline?: boolean;
  fileCollectionId: number;
  onToggleTimeline?: (open: boolean) => void;
  onClose?: () => void;
};

export default function FileCollectionControls({
  showTimeline,
  fileCollectionId,
  onToggleTimeline,
  onClose,
}: FileCollectionControlsProps) {
  const { onApiError } = useAppState();
  const [downloadMessage, setDownloadMessage] = useString(
    'Preparing collection...'
  );
  const [isEditDialogOpen, toggleEditDialog] = useToggle();
  const [isDeleteDialogOpen, toggleDeleteDialog] = useToggle();
  const [isPreparingDownload, togglePreparingDownload] = useToggle();
  const [isDownloadToasterOpen, toggleDownloadToaster] = useToggle();

  const handleDownload = async () => {
    try {
      togglePreparingDownload(true);
      toggleDownloadToaster(true);
      await downloadFileCollection(fileCollectionId, setDownloadMessage);
      togglePreparingDownload(false);
    } catch (err) {
      handleCloseDownloadToaster();
      onApiError(err, 'Error downloading collection');
    }
  };

  const handleCloseDownloadToaster = () => {
    togglePreparingDownload(false);
    toggleDownloadToaster(false);
  };

  return (
    <>
      {/*{isShareDialogOpen && (*/}
      {/*  <ShareFileDialog*/}
      {/*    fileId={fileId}*/}
      {/*    onClose={() => toggleShareDialog(false)}*/}
      {/*  />*/}
      {/*)}*/}
      {isEditDialogOpen && (
        <FileCollectionDialog
          fileCollectionId={fileCollectionId}
          onClose={() => toggleEditDialog()}
        />
      )}
      {isDeleteDialogOpen && (
        <DeleteFileCollectionDialog
          fileCollectionId={fileCollectionId}
          onDelete={onClose}
          onClose={() => toggleDeleteDialog()}
        />
      )}
      <DownloadToaster
        open={isDownloadToasterOpen}
        isLoading={isPreparingDownload}
        message={downloadMessage}
        onClose={handleCloseDownloadToaster}
      />
      <div className={classes.wrapper}>
        {onToggleTimeline && (
          <>
            <OptionButton
              title={`${showTimeline ? 'Hide' : 'Show'} notes`}
              onClick={() => onToggleTimeline(!showTimeline)}>
              <MessagesIcon
                className={twMerge(
                  classes.icon,
                  showTimeline && classes.activeIcon
                )}
              />
            </OptionButton>
            <Divider
              flexItem
              className={classes.divider}
              orientation="vertical"
            />
          </>
        )}
        <OptionButton title="Download" onClick={handleDownload}>
          <DownloadIcon className={classes.icon} />
        </OptionButton>
        {/*<OptionButton title="Share" onClick={toggleShareDialog}>*/}
        {/*  <ShareIcon className={classes.icon} />*/}
        {/*</OptionButton>*/}
        <OptionButton title="Edit" onClick={toggleEditDialog}>
          <EditIcon className={classes.icon} />
        </OptionButton>
        <OptionButton
          className={classes.deleteButton}
          title="Delete"
          onClick={toggleDeleteDialog}>
          <DeleteIcon className={twMerge(classes.icon, classes.deleteIcon)} />
        </OptionButton>

        {onClose && (
          <>
            <Divider
              flexItem
              className={classes.divider}
              orientation="vertical"
            />
            <IconButton aria-label="close" onClick={onClose}>
              <CloseIcon className={classes.icon} />
            </IconButton>
          </>
        )}
      </div>
    </>
  );
}
